/**
 * VAL-STRIPE-GATE-1 - the owner's pay screen on step 5 "Your value".
 *
 * Shown instead of the value when the server answers 402 (owner ruling 2026-10-29:
 * no value before payment). Each option sends the browser to Stripe's own hosted
 * checkout page; nothing about the card touches this app. Coming back is NOT proof
 * of payment - the screen re-asks the server, which unlocks only once Stripe's signed
 * webhook has recorded the payment.
 *
 * VAL-PAY-FLOW-1: reached only after the estimate disclaimer is accepted. The server
 * refuses the options and the checkout without it (409 `disclaimer_required`); if that
 * ever happens here (e.g. the disclaimer text changed), `onDisclaimerRequired` sends
 * the owner back to the disclaimer instead of showing an error.
 *
 * CLIENT_TEXT is PROPOSED wording, pending owner approval (VAL-STRIPE-GATE-1 report).
 */
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  fetchPaymentInfo, startCheckout, valueBlockerFromError, type PaymentInfo, type ValueBlocker,
} from '@/api/valuationPayment'
import { isDisclaimerRequiredError } from '@/api/valuationDisclaimer'
import { formatApiError } from '@/utils/httpErrors'
import { CLIENT_TEXT as BLOCKED_TEXT, ValueBlocked } from './_ValueBlocked'

export const CLIENT_TEXT = {
  'pay.title': 'Your value is ready',
  'pay.body': 'Choose an option and pay to see your value, its range and how it was built.',
  // VAL-TIERS-1-2 (proposed): the unlock rule, in one line.
  'pay.higher': 'Each option includes everything in the options before it.',
  'pay.button': 'Pay {amount}',
  'pay.secure': 'You will pay on a secure Stripe page and come straight back here.',
  'pay.cards': 'We accept Visa and Mastercard.',
  'pay.notConfigured': 'Online payment is not available yet.',
  'pay.cancelled': 'Payment was cancelled. You have not been charged.',
  'pay.confirming': 'Thank you. We are confirming your payment - your value will appear in a moment.',
  'pay.slow': 'Your payment is still being confirmed. Refresh this page in a minute.',
} as const

export type CheckoutReturn = 'done' | 'cancelled' | null

export function formatCents(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() })
    .format(cents / 100)
}

/** VAL-FIX-PAY-BUGS-1 bug 2: an owner never reads a status or an error code. A router's
 *  own `detail` sentence is client text already (formatApiError returns it alone); the
 *  coded envelope is not, so it becomes the plain "try again" line. */
function payRefusalWords(err: unknown): string {
  const coded = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error
  return coded ? BLOCKED_TEXT['blocked.retry'] : formatApiError(err)
}

export function PayForValue({ engagementId, returned, slow = false, onDisclaimerRequired, onPaid }: {
  engagementId: string
  returned: CheckoutReturn
  /** The return from Stripe was a while ago and the payment is still not recorded. */
  slow?: boolean
  /** VAL-PAY-FLOW-1: the server wants the estimate disclaimer accepted first. */
  onDisclaimerRequired?: () => void
  /** VAL-FIX-PAY-BUGS-1 bug 1: the server says it is paid (the webhook landed after the
   *  value was refused) - ask for the value again instead of offering payment. */
  onPaid?: () => void
}) {
  const [info, setInfo] = useState<PaymentInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<ValueBlocker | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [reload, setReload] = useState(0)
  const toDisclaimer = useRef(onDisclaimerRequired)
  toDisclaimer.current = onDisclaimerRequired
  const toPaid = useRef(onPaid)
  toPaid.current = onPaid

  useEffect(() => {
    let live = true
    setError(null)
    fetchPaymentInfo(engagementId)
      .then(res => {
        if (!live) return
        setInfo(res)
        if (res.paid && toPaid.current) toPaid.current()
      })
      .catch((err: unknown) => {
        if (!live) return
        if (isDisclaimerRequiredError(err) && toDisclaimer.current) toDisclaimer.current()
        else setError(payRefusalWords(err))
      })
    return () => { live = false }
  }, [engagementId, reload])

  const pay = async (tier: string) => {
    setBusy(tier)
    setError(null)
    try {
      window.location.assign(await startCheckout(engagementId, tier))
    } catch (err: unknown) {
      setBusy(null)
      const blocker = valueBlockerFromError(err)
      if (isDisclaimerRequiredError(err) && toDisclaimer.current) toDisclaimer.current()
      else if (blocker) setBlocked(blocker)
      else setError(payRefusalWords(err))
    }
  }

  const notReady = blocked ?? (info && info.ready === false ? info.blocker ?? null : null)
  if (notReady || (info && info.ready === false)) {
    // P1: the value cannot be worked out yet - no payment is offered; say where to fix it.
    return <ValueBlocked engagementId={engagementId} blocker={notReady} paidAt={info?.paid_at}
                         onRetry={() => { setBlocked(null); setReload(n => n + 1) }} />
  }

  if (returned === 'done' || info?.paid) {
    return (
      <Card data-testid="pay-confirming">
        <CardContent className="p-6 text-sm text-pal-ink-body">
          {slow ? CLIENT_TEXT['pay.slow'] : CLIENT_TEXT['pay.confirming']}
        </CardContent>
      </Card>
    )
  }

  if (info === null) {
    // Bug 1: never an empty box while the pay screen loads.
    return (
      <Card data-testid="pay-loading">
        <CardContent className="space-y-3 p-6 text-sm text-pal-ink-body">
          {error ? (
            <>
              <p>{error}</p>
              <Button size="sm" variant="outline" onClick={() => setReload(n => n + 1)}>
                {BLOCKED_TEXT['blocked.retryButton']}
              </Button>
            </>
          ) : (
            <>
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-full" />
            </>
          )}
        </CardContent>
      </Card>
    )
  }

  const off = !info.payments_configured
  return (
    <Card data-testid="pay-for-value">
      <CardHeader>
        <CardTitle>{CLIENT_TEXT['pay.title']}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-pal-ink-body">
        {returned === 'cancelled' && <p className="text-pal-warning-text">{CLIENT_TEXT['pay.cancelled']}</p>}
        <p>{CLIENT_TEXT['pay.body']}</p>
        <p>{CLIENT_TEXT['pay.higher']}</p>
        {off && <p className="text-pal-warning-text">{CLIENT_TEXT['pay.notConfigured']}</p>}
        <div className="flex flex-wrap gap-3">
          {(info?.options ?? []).map(o => (
            <div key={o.tier} className="rounded-lg border border-pal-line p-4">
              <p className="font-medium text-pal-ink">{o.title ?? o.label}</p>
              {o.description && <p className="mt-1 max-w-xs text-pal-ink-soft">{o.description}</p>}
              <Button className="mt-3" disabled={off || busy !== null} onClick={() => { void pay(o.tier) }}>
                {CLIENT_TEXT['pay.button'].replace('{amount}', formatCents(o.amount_cents, o.currency))}
              </Button>
            </div>
          ))}
        </div>
        {!off && <p className="text-xs text-pal-ink-muted">{CLIENT_TEXT['pay.secure']}</p>}
        {!off && <p className="text-xs text-pal-ink-muted">{CLIENT_TEXT['pay.cards']}</p>}
        {error && <p className="text-pal-error-text">{error}</p>}
      </CardContent>
    </Card>
  )
}
